/** Campaign beneficiary funnel.
 *
 *  Bands, not a ring, and the bands PARTITION the total.
 *
 *  Reported 2026-09-27 from the live page: the donut's centre read
 *  1,007 while its slices drew 1,000, and its legend summed to 1,016.
 *  Two independent causes:
 *
 *    - `no_show` and `declined` are counted in `total` and had no
 *      slice, so seven beneficiaries were simply absent from a chart
 *      whose centre claimed to count them;
 *    - `rescheduled` is an OVERLAY, not a stage — a membership that
 *      rescheduled is still in whichever stage it now occupies (14 of
 *      16 were, on that campaign) — yet it sat in the legend as a peer
 *      of the three stages.
 *
 *  So: every status gets a band, churn gets its own line below, and the
 *  component asserts the arithmetic rather than trusting the caller.
 *
 *  A ring was also the wrong shape for the data. At 94% one status it
 *  renders as a near-solid circle; a band says the same thing smaller
 *  and stays readable when one stage dominates.
 */
import { useTranslation } from "react-i18next";

import {
  COLORS,
  funnelStages,
  unaccounted,
  type FunnelBreakdown,
} from "./campaignFunnel";

export function CampaignFunnelBars({ data }: { data: FunnelBreakdown }) {
  const { t } = useTranslation();
  const stages = funnelStages(data);
  const other = unaccounted(data);
  const denom = data.total || 1;
  const pct = (n: number) => (n / denom) * 100;

  return (
    <div className="space-y-3">
      <div
        className="flex h-3 overflow-hidden rounded-full bg-nb-bg/60"
        role="img"
        aria-label={t("campaigns.detail.funnelTitle")}
      >
        {stages.map((s) => (
          <span
            key={s.key}
            className={COLORS[s.key]}
            style={{ width: `${pct(s.value)}%` }}
          />
        ))}
        {other > 0 && (
          <span className="bg-nb-text-3" style={{ width: `${pct(other)}%` }} />
        )}
      </div>

      {/* One per row, not two columns. In the 19rem rail a two-column
          legend clipped "Not booked" to "Not boo…" — the label carries
          the meaning, so it is the number that can share a line. */}
      <ul className="flex flex-col gap-1 text-sm">
        {stages.map((s) => (
          <li key={s.key} className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className={`h-2 w-2 shrink-0 rounded-sm ${COLORS[s.key]}`}
            />
            <span className="truncate text-nb-text-2">
              {t(`campaigns.detail.funnel_${s.key}`)}
            </span>
            <span className="ml-auto font-mono tabular-nums">{s.value}</span>
          </li>
        ))}
        {other > 0 && (
          <li className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="h-2 w-2 shrink-0 rounded-sm bg-nb-text-3"
            />
            <span className="truncate text-nb-text-2">
              {t("campaigns.detail.funnelOther")}
            </span>
            <span className="ml-auto font-mono tabular-nums">{other}</span>
          </li>
        )}
      </ul>

      {/* Churn, kept out of the bands on purpose — see the note above. */}
      {data.rescheduled > 0 && (
        <p className="flex items-center gap-2 border-t border-nb-border pt-2.5 text-xs text-nb-text-2">
          <span aria-hidden>↻</span>
          {t("campaigns.detail.funnelRescheduledNote", {
            count: data.rescheduled,
          })}
        </p>
      )}
    </div>
  );
}
